"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "freezer-meat-storage-calculator",
  "freezer-vacuum-sealer-calculator",
  "freezer-energy-cost-calculator",
  "freezer-btu-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Freezer Storage Capacity Calculator",
  category: "Calculator" as const,
  mediumDescription:
    "Translate cubic feet into real food: how many pounds of meat, how many frozen meals, how many pints of ice cream a given freezer holds. Sized using a 30–40 lb-per-cu-ft chest-freezer rule of thumb.",
  faqs: [
    { question: "How many pounds of food fit in 1 cubic foot of freezer?", answer: "About 35 lbs is the common rule of thumb. Chest freezers pack tighter (~40 lb/cu ft) because there's no shelving; uprights run lower (~30 lb/cu ft) due to shelves and bins." },
    { question: "How much can I fit in a 15 cu ft chest freezer?", answer: "Roughly 600 lb of food at 40 lb/cu ft — enough for a quarter beef plus 4–5 months of family meal prep." },
    { question: "Why is a chest freezer more efficient at holding food?", answer: "No shelves means you can use the entire interior volume, and cold air doesn't fall out when you open the lid. The trade-off is harder organization." },
    { question: "Should I keep my freezer 75% full?", answer: "Yes — ENERGY STAR recommends keeping freezers around 75% full because frozen food acts as thermal mass and helps the unit hold cold during the compressor's off cycle." },
    { question: "How do vacuum-sealed packages change capacity?", answer: "Vacuum-sealed flat packages let you store roughly 30–40% more food in the same freezer because they remove air pockets and stack flat like books." },
    { question: "What temperature should I store frozen food at?", answer: "USDA recommends 0°F (-18°C). At that temperature food stays safe indefinitely — quality declines over time but it never becomes unsafe." },
  ],
  relatedTools: ["freezer-meat-storage-calculator", "freezer-vacuum-sealer-calculator", "freezer-energy-cost-calculator"],
}

export function FreezerStorageCapacityPageClient() {
  const [cubicFeet, setCubicFeet] = useState("15")
  const [freezerType, setFreezerType] = useState("chest")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    // A freezer cabinet runs ~1 cu ft (compact personal) to ~100 (commercial chest).
    const size = readQuantity(cubicFeet, { min: 1, max: 100 })
    if (size === null) return null

    const poundsPerCuFt = freezerType === "chest" ? 40 : freezerType === "upright" ? 30 : 35
    const totalPounds = size * poundsPerCuFt
    const beefQuarters = Math.floor(totalPounds / 150)
    const chickenPounds = Math.floor(totalPounds / 3) * 3
    const frozenMeals = Math.floor(totalPounds / 1.5)
    const iceCreamPints = Math.floor(totalPounds / 0.5)
    const efficiency = freezerType === "chest" ? 95 : freezerType === "upright" ? 75 : 85

    return {
      totalPounds,
      beefQuarters,
      chickenPounds,
      frozenMeals,
      iceCreamPints,
      efficiency,
    }
  }, [cubicFeet, freezerType])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="What does"
      heroTitleItalic="cubic feet"
      heroTitlePost="actually fit in your freezer?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~15 sec", "Chest 40 / upright 30 lb per cu ft"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "40 lb", label: "Per cu ft", note: "Chest packed" },
            { value: "30 lb", label: "Per cu ft", note: "Upright shelves" },
            { value: "75%", label: "Target fullness", note: "ENERGY STAR" },
            { value: "0°F", label: "Storage target", note: "USDA" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "freezer-storage-capacity-calculator")}
      cta={{
        title: "Need more room than you have?",
        body: "Browse upright freezers — easy organization, big interior volume, modern energy use.",
        buttonLabel: "Browse upright freezers",
        buttonHref: "/collections/upright-freezers",
      }}
    >
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Volume.
                <br />
                Configuration.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Chest freezers pack ~40 lb per cu ft. Upright shelves give up density for accessibility.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Freezer size
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={100}
                      step="0.1"
                      value={cubicFeet}
                      onChange={(e) => setCubicFeet(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">cu ft</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Freezer type
                  </label>
                  <select
                    value={freezerType}
                    onChange={(e) => setFreezerType(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="chest">Chest freezer (40 lb / cu ft)</option>
                    <option value="upright">Upright (30 lb / cu ft)</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Chest packs denser; upright is easier to navigate.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Total food capacity
                  </div>
                  <div className="font-black" style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}>
                    {result?.totalPounds ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">lbs</span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result ? `${result.efficiency}% packing efficiency vs theoretical maximum.` : "Enter freezer details to size capacity."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Frozen meals</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.frozenMeals ?? "—"}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Quarter beef</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.beefQuarters ?? "—"}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {result && (
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="bg-white rounded-xl p-5 border border-gray-200">
                <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#ff9951] mb-1">OR</div>
                <div className="text-[24px] font-black text-gray-900">{result.chickenPounds} lbs</div>
                <div className="text-[13px] text-gray-600">whole chickens</div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-200">
                <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#ff9951] mb-1">OR</div>
                <div className="text-[24px] font-black text-gray-900">{result.iceCreamPints} pints</div>
                <div className="text-[13px] text-gray-600">ice cream</div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-200">
                <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#ff9951] mb-1">OR</div>
                <div className="text-[24px] font-black text-gray-900">{result.frozenMeals}</div>
                <div className="text-[13px] text-gray-600">prepped meals (1.5 lb each)</div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Pick the unit"
          title="Upright freezers — easier organization"
          description="Shelves and bins make finding the right cut effortless. Trade ~25% packing density for full visibility."
          collectionType="upright-freezer"
          fallbackCollectionType="chest-freezer"
        />
      </section>

      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                Lb per cu ft, <em className="italic text-[#ff9951]">honestly.</em>
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Density rule-of-thumb based on long-standing chest-freezer industry guidance and USDA bulk-meat planning literature.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Density per type", d: "Chest freezers ~40 lb per cu ft (no shelves). Upright freezers ~30 lb per cu ft (shelves, bins, door). Mixed configurations ~35 lb per cu ft." },
                { n: "02", t: "Total capacity", d: "Total lbs = cu ft × density. Then we map to representative items: 1 quarter beef ≈ 150 lb, 1 frozen meal ≈ 1.5 lb, 1 pint ice cream ≈ 0.5 lb." },
                { n: "03", t: "Packing efficiency", d: "Chest freezers max out around 95% of theoretical capacity. Uprights cap closer to 75% because shelving and door bins consume usable volume." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Chest freezers — maximum density"
          description="Pack 40 lb per cu ft and use 20% less energy than uprights. Trade-off is digging through layers to find what you want."
          collectionType="chest-freezer"
          fallbackCollectionType="upright-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
